import { Box, Link as MuiLink, Paper, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { REPORT_GROUPS } from '@app/shared';
import type { ReportGroup } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';

interface CatalogueEntry {
  key: string;
  group: ReportGroup;
  name: string;
  code: string;
  /** Set for a report added after a deployment: listed once its release switch is on. */
  feature?: string;
}

/**
 * All Reports (PC428): the report catalogue in the reference's three groups. It lists the
 * reports this application can fill and the caller holds the code for.
 */
export function AllReportsPage() {
  const featureOn = useFeatureReleased();
  const catalogue = useQuery({
    queryKey: ['reports', 'catalogue'],
    queryFn: () => api.get<{ data: CatalogueEntry[] }>('/reports'),
    select: (r) => r.data,
  });

  return (
    <Box>
      <PageHeader title="All Reports" code="PC428" />
      {catalogue.error ? (
        <ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />
      ) : !catalogue.data ? (
        <LoadingState />
      ) : (
        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
        >
          {REPORT_GROUPS.map(([group, title]) => {
            const reports = catalogue.data.filter(
              (r) => r.group === group && (!r.feature || featureOn(r.feature)),
            );
            return (
              <Paper
                key={group}
                variant="outlined"
                component="section"
                aria-label={title}
                sx={{ p: 2 }}
              >
                <Typography variant="h3" sx={{ mb: 1 }}>
                  {title}
                </Typography>
                {reports.length === 0 ? (
                  <Typography color="text.secondary">No report available to you.</Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.75 }}>
                    {reports.map((r) => (
                      <li key={r.key}>
                        <MuiLink component={Link} to={`/reports/${r.key}`}>
                          {r.name} ({r.code})
                        </MuiLink>
                      </li>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
